/**
 * EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
 *
 * Reusable editor for a list of client-facing crew/equipment
 * "requirements" attached to a shift. Used in both the new-project
 * request form and the shift-change request dialog.
 *
 * Each row: { label, category, quantity, unit, notes, client_catalog_key }.
 * Internal/admin mapping fields are NEVER set client-side.
 */
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2 } from "lucide-react";

// Curated catalogue surfaced as a `<datalist>` so users can either pick
// from suggestions or free-type a custom requirement. The free-text
// `label` is what persists.
export const CLIENT_REQUIREMENT_CATALOG = [
  { key: "general_crew", label: "General Crew / Road", category: "crew", unit: "crew" },
  { key: "audio_crew", label: "Audio Crew", category: "crew", unit: "crew" },
  { key: "lighting_crew", label: "Lighting Crew", category: "crew", unit: "crew" },
  { key: "video_crew", label: "Video Crew", category: "crew", unit: "crew" },
  { key: "rigger", label: "Rigger", category: "crew", unit: "crew" },
  { key: "stagehand", label: "Stagehand", category: "crew", unit: "crew" },
  { key: "carpenter", label: "Carpenter", category: "crew", unit: "crew" },
  { key: "forklift_driver", label: "Forklift Driver", category: "special_role", unit: "crew" },
  { key: "forklift", label: "Forklift", category: "equipment", unit: "unit" },
  { key: "scissor_lift", label: "Scissor Lift", category: "equipment", unit: "unit" },
  { key: "truck", label: "Truck", category: "vehicle", unit: "unit" },
  { key: "van", label: "Van", category: "vehicle", unit: "unit" },
];

const CATEGORIES = [
  { value: "crew", label: "Crew" },
  { value: "equipment", label: "Equipment" },
  { value: "vehicle", label: "Vehicle" },
  { value: "special_role", label: "Special role" },
  { value: "other", label: "Other" },
];

const emptyRequirement = () => ({
  label: "",
  category: "crew",
  quantity: 1,
  unit: "",
  notes: "",
  client_catalog_key: "",
});

export function ShiftRequirementsEditor({
  shiftIndex,
  value,
  onChange,
  testIdPrefix = "shift",
}) {
  const rows = Array.isArray(value) ? value : [];
  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (idx) => {
    onChange(rows.filter((_, i) => i !== idx));
  };
  const add = () => {
    onChange([...rows, emptyRequirement()]);
  };

  // When a curated label is typed/picked, auto-fill category + unit + key
  // (only when those fields are empty — never clobber user input).
  const handleLabelChange = (idx, val) => {
    const match = CLIENT_REQUIREMENT_CATALOG.find(
      (c) => c.label.toLowerCase() === (val || "").trim().toLowerCase(),
    );
    const current = rows[idx] || emptyRequirement();
    const patch = { label: val };
    if (match) {
      if (!current.category || current.category === "crew") patch.category = match.category;
      if (!current.unit) patch.unit = match.unit;
      patch.client_catalog_key = match.key;
    } else {
      patch.client_catalog_key = "";
    }
    update(idx, patch);
  };

  const datalistId = `client-requirement-options-${shiftIndex ?? "x"}`;

  return (
    <div
      className="space-y-2 pt-2 border-t border-border/40"
      data-testid={`${testIdPrefix}-requirements-editor-${shiftIndex ?? 0}`}
    >
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">Requested requirements</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={add}
          data-testid={`${testIdPrefix}-add-requirement-${shiftIndex ?? 0}`}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add requirement
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Pick from suggestions or type your own — e.g. <em>4 × General
        Crew / Road, 1 × Audio Crew, 1 × Forklift Driver</em>. These are
        sent as a request to our team, not a confirmed booking.
      </p>
      <datalist id={datalistId}>
        {CLIENT_REQUIREMENT_CATALOG.map((opt) => (
          <option key={opt.key} value={opt.label} />
        ))}
      </datalist>

      {rows.length === 0 ? (
        <div
          className="rounded border border-dashed border-border/60 p-3 text-[11px] text-muted-foreground italic"
          data-testid={`${testIdPrefix}-requirements-empty-${shiftIndex ?? 0}`}
        >
          No specific requirements — our team will plan a standard crew
          based on the shift details above.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-1 items-start rounded border border-border/40 bg-accent/10 p-2"
              data-testid={`${testIdPrefix}-requirement-row-${shiftIndex ?? 0}-${idx}`}
            >
              <div className="col-span-12 sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Qty</Label>
                <Input
                  type="number"
                  min={0}
                  step="1"
                  inputMode="numeric"
                  value={r.quantity ?? 1}
                  onChange={(e) =>
                    update(idx, {
                      quantity: Math.max(0, parseFloat(e.target.value || "0") || 0),
                    })
                  }
                  className="h-8 text-sm"
                  data-testid={`${testIdPrefix}-req-qty-${shiftIndex ?? 0}-${idx}`}
                />
              </div>
              <div className="col-span-12 sm:col-span-5">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Item / role
                </Label>
                <Input
                  list={datalistId}
                  value={r.label ?? ""}
                  onChange={(e) => handleLabelChange(idx, e.target.value)}
                  placeholder="e.g. Audio Crew, Forklift Driver…"
                  maxLength={200}
                  className="h-8 text-sm"
                  data-testid={`${testIdPrefix}-req-label-${shiftIndex ?? 0}-${idx}`}
                />
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Category
                </Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                  value={r.category ?? "crew"}
                  onChange={(e) => update(idx, { category: e.target.value })}
                  data-testid={`${testIdPrefix}-req-category-${shiftIndex ?? 0}-${idx}`}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-6 sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Unit</Label>
                <Input
                  value={r.unit ?? ""}
                  onChange={(e) => update(idx, { unit: e.target.value })}
                  placeholder="crew, unit…"
                  maxLength={32}
                  className="h-8 text-sm"
                  data-testid={`${testIdPrefix}-req-unit-${shiftIndex ?? 0}-${idx}`}
                />
              </div>
              <div className="col-span-12 sm:col-span-1 flex sm:justify-end pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                  onClick={() => remove(idx)}
                  aria-label="Remove requirement"
                  data-testid={`${testIdPrefix}-req-remove-${shiftIndex ?? 0}-${idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="col-span-12">
                <Input
                  value={r.notes ?? ""}
                  onChange={(e) => update(idx, { notes: e.target.value })}
                  placeholder="Notes (optional)"
                  maxLength={1000}
                  className="h-8 text-sm"
                  data-testid={`${testIdPrefix}-req-notes-${shiftIndex ?? 0}-${idx}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Compact read-only summary for shift detail pages.
 * Example: `4 × General Crew · 1 × Audio Crew · 1 × Forklift Driver`
 */
export function RequirementsSummaryInline({ value, className = "" }) {
  const rows = Array.isArray(value) ? value.filter((r) => r && r.label) : [];
  if (rows.length === 0) return null;
  return (
    <div
      className={`flex flex-wrap gap-1.5 ${className}`}
      data-testid="shift-requirements-summary"
    >
      {rows.map((r, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px]"
          data-testid={`shift-requirements-summary-item-${i}`}
        >
          <strong className="font-semibold">{Number(r.quantity || 0)} ×</strong>
          <span>{r.label}</span>
          {r.unit && (
            <span className="text-muted-foreground">({r.unit})</span>
          )}
        </span>
      ))}
    </div>
  );
}

export default ShiftRequirementsEditor;
